"use client";

import { Accordion, Container, Group, Text, Title, Divider } from "@mantine/core";
import { IconBolt } from "@tabler/icons-react";
import { TOKEN_UI_ENABLED } from "@/lib/shared/featureFlags";

type AccordionLabelProps = {
  label: string;
};

// Two of these entries (KURBO mode, daily tokens) exist purely to explain the
// token mechanic and don't make sense to show before that mechanic is live;
// the "plan differences" entry mentions tokens alongside still-relevant
// content, so only its token clause is swapped rather than hiding the whole
// entry.
const faqsList = [
  {
    id: "plan-differences",
    label: "WHAT'S THE DIFFERENCE BETWEEN THE FREE AND PRO PLANS?",
    content: TOKEN_UI_ENABLED ? (
      <>
        Every signed-in user gets the same core features: unlimited saved filters, favorites,
        collections, price drop monitors, saved filter push notifications (hourly and 2-hour
        intervals), and daily email summaries.
        <br />
        <br />
        KURB Pro adds a larger daily token budget, giving you more real-time searches and price drop
        monitors each day, plus faster push notification intervals (Instant, 15-minute, and
        30-minute). Pro members also get early access to features in development and invitations to
        beta group conversations on Discord.
      </>
    ) : (
      <>
        Every signed-in user gets the same core features: unlimited saved filters, favorites,
        collections, price drop monitors, saved filter push notifications (hourly and 2-hour
        intervals), and daily email summaries.
        <br />
        <br />
        KURB Pro adds faster push notification intervals (Instant, 15-minute, and 30-minute). Pro
        members also get early access to features in development and invitations to beta group
        conversations on Discord.
      </>
    ),
  },
  ...(TOKEN_UI_ENABLED
    ? [
        {
          id: "plan-faq-kurbo",
          label: "WHAT IS KURBO MODE?",
          content: (
            <>
              KURBO is the real-time search layer built into KURB. When you enable it on the search
              page, it surfaces listings added in the last hour that match your active filters, the
              freshest items on the market, before the crowd finds them.
              <br />
              <br />
              Each KURBO search draws one token from your daily budget. Your token count is shown as
              a{" "}
              <IconBolt
                size={14}
                stroke={1.75}
                style={{ verticalAlign: "middle", display: "inline" }}
              />{" "}
              in the navigation bar. Tokens reset at midnight every day.
            </>
          ),
        },
        {
          id: "plan-faq-tokens",
          label: "HOW DO DAILY TOKENS WORK?",
          content: (
            <>
              Tokens are the currency that powers KURBO mode and price drop monitors. You receive a
              fresh allocation every day at midnight, and you can track your remaining balance via
              the{" "}
              <IconBolt
                size={14}
                stroke={1.75}
                style={{ verticalAlign: "middle", display: "inline" }}
              />{" "}
              in the nav bar or under Account → Billing.
              <br />
              <br />
              When your daily tokens run out, blurred teaser cards appear in your search results
              showing a preview of what KURBO has found. Upgrading to Pro or Premium gives you a
              larger daily budget so you can do more searches and set up more monitors.
            </>
          ),
        },
      ]
    : []),
  {
    id: "plan-faq-2",
    label: "WHAT ARE SAVED FILTERS?",
    content:
      "Saved filters let you define search criteria (designer, category, size, price range) and revisit them any time without resetting your search. You can set up push notifications or daily email summaries against each saved filter so KURB alerts you when new listings match.",
  },
  {
    id: "plan-faq-3",
    label: "HOW DO PUSH NOTIFICATIONS WORK?",
    content: (
      <>
        When a new listing matches one of your saved filters, KURB can send a push notification
        straight to your device. All signed-in users can choose hourly or 2-hour notification
        intervals.
        <br />
        <br />
        KURB Pro and Premium unlock faster intervals: Instant, 15-minute, and 30-minute, so you can
        be notified as soon as a listing hits the market.
      </>
    ),
  },
  {
    id: "plan-faq-4",
    label: "WHAT'S INCLUDED IN THE DAILY EMAIL SUMMARIES?",
    content:
      "Email summaries give you a digest of new listings that match your saved filters, delivered to your inbox. They're available to all signed-in users so you can stay updated without having to check the app constantly.",
  },
  {
    id: "plan-faq-5",
    label: "ARE THERE DEVICE LIMITATIONS?",
    content:
      "KURB works on all smartphones and computers. Push notifications require a modern browser with notification permissions enabled. We address any compatibility issues as soon as we become aware of them.",
  },
  {
    id: "plan-faq-6",
    label: "CAN I SWITCH PLANS ANYTIME?",
    content: (
      <>
        Yes. You can upgrade or downgrade at any time; all your saved filters, favorites, and
        collections stay intact.
        <br />
        <br />
        If you have questions about which plan is right for you, reach out via Discord, Instagram,
        or email and we&apos;ll help.
      </>
    ),
  },
  {
    id: "plan-faq-7",
    label: "WHAT HAPPENS IF I CANCEL KURB PRO?",
    content: TOKEN_UI_ENABLED
      ? "Your saved filters, favorites, and collections remain untouched. You'll move to the free token budget and your push notification intervals will revert to hourly and 2-hour. Instant, 15-minute, and 30-minute intervals will be greyed out in your settings until you resubscribe."
      : "Your saved filters, favorites, and collections remain untouched. Your push notification intervals will revert to hourly and 2-hour. Instant, 15-minute, and 30-minute intervals will be greyed out in your settings until you resubscribe.",
  },
  {
    id: "plan-faq-8",
    label: "WHERE SHOULD I GO IF I HAVE PROBLEMS WITH KURB?",
    content:
      "The best way to get immediate help is via our Discord server, Instagram, or email at contact@kurb.online.",
  },
];

function AccordionLabel({ label }: AccordionLabelProps) {
  return (
    <Group wrap="nowrap">
      <Text mb="sm" size="sm">
        {label}
      </Text>
    </Group>
  );
}

const PlansFaqs = () => {
  const items = faqsList.map((item) => (
    <Accordion.Item value={item.id} key={item.id} color="white">
      <Accordion.Control>
        <AccordionLabel {...item} />
      </Accordion.Control>
      <Accordion.Panel>
        <Text size="sm" mt="md">
          {item.content}
        </Text>
      </Accordion.Panel>
    </Accordion.Item>
  ));

  return (
    <Container mt="54px">
      <Title order={2} ta="left" mb="md">
        FREQUENTLY ASKED QUESTIONS
      </Title>
      <Divider color="black" my="md" />
      <Accordion variant="separated" defaultValue="customize" chevronPosition="right">
        {items}
      </Accordion>
    </Container>
  );
};
export default PlansFaqs;
